/**
 * Chat Created Stack Popover
 *
 * Header gallery for everything Losi created in this chat — generated images
 * plus notes, tasks, plans, virtual files, etc. Images keep photo cards;
 * other kinds use icon cards in the same leaning stack.
 */

'use client'

import {
  Calendar,
  CheckSquare,
  Download,
  FileText,
  FileType2,
  Folder,
  ImageIcon,
  LayoutTemplate,
  ListChecks,
  MessageSquare,
  Palette,
  StickyNote,
  Table2,
  Boxes
} from 'lucide-react'
import { useMemo, useState, type ComponentType } from 'react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  chatCreatedArtifactKindLabel,
  type ChatCreatedArtifact,
  type ChatCreatedArtifactKind
} from '@/lib/chat/created-artifacts'
import { getSafeRenderableImageUrl } from '@/lib/media/safe-url'
import { cn } from '@/lib/utils'

export type { ChatCreatedArtifact }

export interface ChatCreatedStackPopoverProps {
  artifacts: ChatCreatedArtifact[]
  onOpen: (artifact: ChatCreatedArtifact) => void
  onDownloadImages?: (images: ChatCreatedArtifact[]) => void
  className?: string
  disabled?: boolean
}

const STACK_CARD_CLASSES = [
  'left-[15px] top-[5px] rotate-[7deg] scale-[0.9] opacity-75',
  'left-[8px] top-[4px] rotate-[3deg] scale-[0.95] opacity-90',
  'left-0.5 top-1 -rotate-[2deg]'
] as const

const KIND_ICON: Partial<
  Record<ChatCreatedArtifactKind, ComponentType<{ className?: string }>>
> = {
  image: ImageIcon,
  plan: ListChecks,
  virtual_file: FileText,
  pdf: FileType2,
  note: StickyNote,
  task: CheckSquare,
  sheet: Table2,
  design: Palette,
  whiteboard: LayoutTemplate,
  sprint: Calendar,
  folder: Folder,
  conversation: MessageSquare,
  chat: MessageSquare,
  event: Calendar,
  file: FileText,
  collection: Folder,
  space: Boxes
}

const KIND_TINT: Partial<Record<ChatCreatedArtifactKind, string>> = {
  image: 'from-sky-500/20 to-sky-500/5 text-sky-700 dark:text-sky-300',
  plan: 'from-violet-500/25 to-violet-500/5 text-violet-700 dark:text-violet-300',
  virtual_file:
    'from-slate-500/20 to-slate-500/5 text-slate-700 dark:text-slate-300',
  pdf: 'from-rose-500/20 to-rose-500/5 text-rose-700 dark:text-rose-300',
  note: 'from-amber-500/25 to-amber-500/5 text-amber-800 dark:text-amber-300',
  task: 'from-emerald-500/25 to-emerald-500/5 text-emerald-700 dark:text-emerald-300',
  sheet: 'from-teal-500/20 to-teal-500/5 text-teal-700 dark:text-teal-300',
  design: 'from-fuchsia-500/20 to-fuchsia-500/5 text-fuchsia-700 dark:text-fuchsia-300',
  whiteboard:
    'from-indigo-500/20 to-indigo-500/5 text-indigo-700 dark:text-indigo-300',
  sprint: 'from-orange-500/20 to-orange-500/5 text-orange-700 dark:text-orange-300',
  folder: 'from-yellow-500/20 to-yellow-500/5 text-yellow-800 dark:text-yellow-300',
  conversation:
    'from-blue-500/20 to-blue-500/5 text-blue-700 dark:text-blue-300',
  chat: 'from-blue-500/20 to-blue-500/5 text-blue-700 dark:text-blue-300',
  event: 'from-rose-500/20 to-rose-500/5 text-rose-700 dark:text-rose-300',
  file: 'from-slate-500/20 to-slate-500/5 text-slate-700 dark:text-slate-300',
  collection:
    'from-yellow-500/20 to-yellow-500/5 text-yellow-800 dark:text-yellow-300',
  space: 'from-cyan-500/20 to-cyan-500/5 text-cyan-700 dark:text-cyan-300'
}

type StackSlot =
  | { artifact: ChatCreatedArtifact; safeUrl?: string }
  | null

function getArtifactIcon(kind: ChatCreatedArtifactKind) {
  return KIND_ICON[kind] || FileText
}

function getArtifactTint(kind: ChatCreatedArtifactKind) {
  return (
    KIND_TINT[kind] ||
    'from-muted to-muted/40 text-muted-foreground'
  )
}

function ArtifactIconCard({
  artifact,
  className
}: {
  artifact: ChatCreatedArtifact
  className?: string
}) {
  const Icon = getArtifactIcon(artifact.kind)
  return (
    <span
      className={cn(
        'flex size-full items-center justify-center bg-gradient-to-br',
        getArtifactTint(artifact.kind),
        className
      )}
      aria-hidden="true"
    >
      <Icon className="size-[45%]" />
    </span>
  )
}

function ArtifactThumb({
  artifact,
  safeUrl,
  className,
  onError
}: {
  artifact: ChatCreatedArtifact
  safeUrl?: string
  className?: string
  onError?: () => void
}) {
  if (artifact.kind === 'image' && safeUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={safeUrl}
        alt={artifact.alt}
        loading="lazy"
        decoding="async"
        className={cn('size-full object-cover', className)}
        onError={onError}
      />
    )
  }

  return <ArtifactIconCard artifact={artifact} className={className} />
}

export function ChatCreatedStackPopover({
  artifacts,
  onOpen,
  onDownloadImages,
  className,
  disabled = false
}: ChatCreatedStackPopoverProps) {
  const [failedImageIds, setFailedImageIds] = useState<ReadonlySet<string>>(
    new Set()
  )

  const visibleArtifacts = useMemo(() => {
    return artifacts.filter(artifact => {
      if (artifact.kind !== 'image') return true
      if (failedImageIds.has(artifact.id)) return false
      return Boolean(getSafeRenderableImageUrl(artifact.url))
    })
  }, [artifacts, failedImageIds])

  const stackSlots = useMemo((): StackSlot[] => {
    const top = visibleArtifacts.slice(0, 3)
    const slots: StackSlot[] = [top[2] ?? null, top[1] ?? null, top[0] ?? null]
    return slots.map(slot => {
      if (!slot) return null
      if (slot.kind !== 'image') return { artifact: slot }
      const safeUrl = getSafeRenderableImageUrl(slot.url) || undefined
      return { artifact: slot, safeUrl }
    })
  }, [visibleArtifacts])

  const imageArtifacts = useMemo(
    () => visibleArtifacts.filter(a => a.kind === 'image'),
    [visibleArtifacts]
  )

  const hasArtifacts = visibleArtifacts.length > 0
  const triggerLabel = hasArtifacts
    ? `Open ${visibleArtifacts.length} created item${visibleArtifacts.length === 1 ? '' : 's'}`
    : 'No created items'

  const handleImageError = (imageId: string) => {
    setFailedImageIds(prev => {
      const next = new Set(prev)
      next.add(imageId)
      return next
    })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          aria-haspopup="dialog"
          disabled={disabled || !hasArtifacts}
          className={cn(
            'focus-visible:ring-ring group relative inline-flex h-10 w-14 shrink-0 items-center justify-center rounded-lg outline-none transition-transform duration-200 ease-out focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none',
            className
          )}
        >
          <span className="sr-only">{triggerLabel}</span>
          <span
            aria-hidden="true"
            className="bg-foreground/10 absolute inset-x-3 bottom-0 h-1 rounded-full blur-[2px] transition-opacity group-hover:opacity-80"
          />
          {stackSlots.map((slot, index) => (
            <span
              key={slot?.artifact.id ?? `empty-${index}`}
              className={cn(
                'border-background bg-muted absolute h-8 w-10 overflow-hidden rounded-[8px] border shadow-[0_4px_10px_rgba(15,23,42,0.16)] transition-transform duration-200 ease-out group-hover:-translate-y-0.5 motion-reduce:transition-none',
                STACK_CARD_CLASSES[index]
              )}
            >
              {slot ? (
                <ArtifactThumb
                  artifact={slot.artifact}
                  safeUrl={slot.safeUrl}
                  onError={
                    slot.artifact.kind === 'image'
                      ? () => handleImageError(slot.artifact.id)
                      : undefined
                  }
                />
              ) : (
                <span className="text-muted-foreground flex size-full items-center justify-center">
                  <StickyNote className="size-4" aria-hidden="true" />
                </span>
              )}
            </span>
          ))}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        collisionPadding={12}
        className="w-[min(calc(100vw-2rem),420px)] overflow-hidden p-0"
      >
        <div className="flex items-center justify-between gap-3 border-b p-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">Created in this chat</p>
            <p className="text-muted-foreground text-xs">
              {visibleArtifacts.length} item
              {visibleArtifacts.length === 1 ? '' : 's'}
              {imageArtifacts.length > 0
                ? ` · ${imageArtifacts.length} image${imageArtifacts.length === 1 ? '' : 's'}`
                : ''}
            </p>
          </div>
          {onDownloadImages && imageArtifacts.length > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => onDownloadImages(imageArtifacts)}
            >
              <Download className="size-3.5" aria-hidden="true" />
              Images
            </Button>
          ) : null}
        </div>
        <div className="max-h-[min(60vh,460px)] overflow-y-auto p-3">
          {hasArtifacts ? (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {visibleArtifacts.map((artifact, index) => {
                const safeUrl =
                  artifact.kind === 'image'
                    ? getSafeRenderableImageUrl(artifact.url) || undefined
                    : undefined
                const kindLabel = chatCreatedArtifactKindLabel(artifact.kind)
                return (
                  <button
                    key={artifact.id}
                    type="button"
                    aria-label={`Open ${kindLabel}: ${artifact.title}`}
                    className="border-border/70 bg-background hover:border-foreground/20 focus-visible:ring-ring group min-w-0 overflow-hidden rounded-xl border text-left shadow-sm transition-[border-color,box-shadow,transform] duration-200 ease-out hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.99] motion-reduce:transition-none"
                    onClick={() => onOpen(artifact)}
                  >
                    <span className="bg-muted block aspect-[4/3] overflow-hidden">
                      <ArtifactThumb
                        artifact={artifact}
                        safeUrl={safeUrl}
                        className="transition-transform duration-200 ease-out group-hover:scale-[1.03] motion-reduce:transition-none"
                        onError={
                          artifact.kind === 'image'
                            ? () => handleImageError(artifact.id)
                            : undefined
                        }
                      />
                    </span>
                    <span className="block space-y-0.5 p-2">
                      <span className="text-muted-foreground block text-[10px] font-medium uppercase tracking-wide">
                        {kindLabel}
                        {index === 0 ? ' · latest' : ''}
                      </span>
                      <span className="line-clamp-2 text-xs font-medium leading-snug">
                        {artifact.title}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="border-border/70 bg-muted/30 flex min-h-28 flex-col items-center justify-center rounded-lg border border-dashed text-center">
              <StickyNote
                className="text-muted-foreground size-5"
                aria-hidden="true"
              />
              <p className="mt-2 text-sm font-medium">Nothing created yet</p>
              <p className="text-muted-foreground text-xs">
                Images, notes, tasks, and plans from this chat show up here.
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
